// vipps.js
// Resolve backend URL safely: never use localhost on production hosts.
const PROD_BACKEND_URL = 'https://backend.playworld.no';

const isLocalhost = (host) => host === 'localhost' || host === '127.0.0.1';
const isPrivateIp = (host) =>
  /^10\./.test(host) ||
  /^192\.168\./.test(host) ||
  /^172\.(1[6-9]|2\d|3[0-1])\./.test(host);

const getBackendUrl = () => {
  const currentHostname = window.location.hostname;
  const configured = (process.env.REACT_APP_BACKEND_URL || '').trim();
  const configuredIsLocal = /localhost|127\.0\.0\.1/.test(configured);

  // Local/dev host: allow localhost backend.
  if (isLocalhost(currentHostname)) {
    return configured || 'http://localhost:4000';
  }

  // LAN/dev host: allow configured backend (often local tunnel/server).
  if (isPrivateIp(currentHostname)) {
    return configured || `http://${currentHostname}:4000`;
  }

  // Public host: never use localhost even if misconfigured.
  if (configured && !configuredIsLocal) {
    return configured;
  }

  return PROD_BACKEND_URL;
};

export const BACKEND_URL = getBackendUrl();

// accessToken: the logged-in customer's Supabase token, so the backend records
// who is paying and only lets them complete the order. Guests pass none.
export async function createVippsPayment(paymentData, accessToken) {
  const endpoint = `${BACKEND_URL}/create-payment`;
  
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(paymentData),
    });

    if (!response.ok) {
      let error;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          error = await response.json();
          console.error('❌ API Error:', error);
        } catch (parseError) {
          const text = await response.text();
          console.error('❌ API Error (text):', text);
          throw new Error(`API error: ${text.substring(0, 200)}`);
        }
      } else {
        const text = await response.text();
        console.error('❌ API returned non-JSON:', text);
        throw new Error(`API error: ${text.substring(0, 200)}`);
      }
      
      throw new Error(JSON.stringify(error));
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('❌ Payment fetch failed:', error.message);
    throw error;
  }
}

// The backend checks the payment with Vipps and writes the order, tickets,
// klippekort and points. Retries while the payment is still being confirmed
// (402/409/502) - Vipps can lag a few seconds behind the redirect back to the
// app. Guests: no token, path '/api/guest-checkout/complete'.
export async function completeCheckout(reference, accessToken, path = '/api/checkout/complete') {
  let body = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      body: JSON.stringify({ reference }),
    });
    body = await res.json().catch(() => ({}));
    if (res.ok && body.success) return body;
    if (![402, 409, 502].includes(res.status)) break;
    await new Promise(r => setTimeout(r, 2000));
  }
  throw new Error(body?.error || 'Order completion failed');
}

export async function captureVippsPayment({ reference, amountValue }) {
  try {
    const response = await fetch(`${BACKEND_URL}/capture-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reference, amountValue }),
    });

    if (!response.ok) {
      console.error(`Capture failed with status ${response.status}`);
      const error = await response.json().catch(() => ({ error: 'Capture failed' }));
      throw new Error(JSON.stringify(error));
    }

    return await response.json();
  } catch (error) {
    console.error('Capture payment error:', error.message);
    throw error;
  }
}

export async function getPaymentDetails(reference) {
  try {
    const response = await fetch(`${BACKEND_URL}/payment-details/${reference}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Payment details request failed with status ${response.status}`);
      // Return a safe default instead of throwing
      return { state: 'UNKNOWN', reference };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching payment details:', error.message);
    // Return safe default on network error
    return { state: 'UNKNOWN', reference };
  }
}

export async function refundVippsPayment({ reference, amountValue }) {
  const response = await fetch(`${BACKEND_URL}/refund-payment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ reference, amountValue }),
  });

  if (!response.ok) {
    let error;
    try {
      error = await response.json();
    } catch {
      // If response is not JSON, use status text
      error = { error: response.statusText || 'Unknown error' };
    }
    throw new Error(JSON.stringify(error));
  }

  return response.json();
}
